import { getCurrentUser } from '../store.js';
import {
  apiCreateBoardPost,
  apiUploadBoardAttachment,
  apiGetBoardTargetUsers,
  apiGetShiftChangeOptions,
  apiCreateShiftChangeRequest,
  apiListShiftChangeRequests,
  apiPeerApproveShiftChangeRequest,
  apiPeerRejectShiftChangeRequest,
  apiDismissShiftChangeRequest,
} from '../api.js';
import { badgeClassForSlotCategory } from '../types.js';

const BOARD_CATEGORIES = [
  { value: 'comunicazioni', label: 'Comunicazioni' },
  { value: 'eventi', label: 'Eventi' },
  { value: 'budget', label: 'Budget' },
  { value: 'organizzazione', label: 'Organizzazione' },
  { value: 'formazione', label: 'Formazione' },
  { value: 'sicurezza', label: 'Sicurezza' },
  { value: 'qualita', label: 'Qualita' },
  { value: 'protocollo', label: 'Protocollo' },
  { value: 'amministrazione', label: 'Amministrazione' },
];

function isPublishManagerRole(role) {
  return /^(admin|director)$/.test(String(role || '').trim());
}

function doctorLabelFromUser(user) {
  const sigla = String(user?.sigla || '').trim();
  const nome = String(user?.nome || '').trim();
  const cognome = String(user?.cognome || '').trim();
  const full = [sigla, nome, cognome].filter(Boolean).join(' ');
  return full || String(user?.username || '').trim() || 'Medico';
}

function personBadgeLabel(username, sigla) {
  const s = String(sigla || '').trim().toUpperCase();
  if (s) return s;
  const u = String(username || '').trim().toLowerCase();
  return u || '-';
}

function escapeHtml(text) {
  return String(text || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function escapeAttr(text) {
  return escapeHtml(text).replaceAll('"', '&quot;');
}

function formatDateIt(dateIso) {
  const d = String(dateIso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return d || '-';
  return `${d.slice(8, 10)}-${d.slice(5, 7)}-${d.slice(0, 4)}`;
}

function slotLabel(slot) {
  return `${formatDateIt(slot?.date)} · ${(slot?.time_slot || '').toUpperCase()} · ${slot?.name || slot?.category || '-'}`;
}

function timeSlotShort(value) {
  const k = String(value || '').trim().toLowerCase();
  if (k === 'mattino') return 'M';
  if (k === 'pomeriggio') return 'P';
  if (k === 'prolungato') return 'PR';
  if (k === 'notte') return 'N';
  if (k === 'reperibilita_giorno') return 'RG';
  if (k === 'reperibilita_notte') return 'RN';
  return (k || '-').toUpperCase();
}

function slotBadgeHtml(slot) {
  const cls = badgeClassForSlotCategory(slot?.category || '');
  const title = String(slot?.name || slot?.category || '-');
  return `<span class="badge badge-${cls}">${escapeHtml(title)} ${escapeHtml(timeSlotShort(slot?.time_slot))} · ${escapeHtml(formatDateIt(slot?.date))}</span>`;
}

function statusLabel(status) {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'pending_peer') return 'In attesa collega';
  if (s === 'pending') return 'In attesa Direttore';
  if (s === 'approved') return 'Approvata';
  if (s === 'rejected') return 'Rifiutata dal Direttore';
  if (s === 'rejected_by_peer') return 'Rifiutata dal collega';
  return status || '-';
}

function statusClass(status) {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'pending_peer') return 'pending-peer';
  if (s === 'pending') return 'pending-director';
  if (s === 'approved') return 'approved';
  if (s === 'rejected') return 'rejected-director';
  if (s === 'rejected_by_peer') return 'rejected-peer';
  return 'neutral';
}

function isClosedShiftRequest(status) {
  const s = String(status || '').trim().toLowerCase();
  return s === 'approved' || s === 'rejected' || s === 'rejected_by_peer';
}

function optionStyleForSlotCategory(category) {
  const c = String(category || '').trim();
  if (c === 'terapia_intensiva') return 'background:#1d9bf033;color:#1d9bf0;';
  if (c === 'sala_operatoria') return 'background:#00ba7c33;color:#00ba7c;';
  if (c === 'nora') return 'background:#34d39933;color:#34d399;';
  if (c === 'medicina_dolore') return 'background:#ffad1f33;color:#ffad1f;';
  if (c === 'cure_palliative') return 'background:#fb923c33;color:#fb923c;';
  if (c === 'direzione') return 'background:#7856ff33;color:#a78bfa;';
  if (c === 'formazione') return 'background:#818cf833;color:#a5b4fc;';
  return '';
}

function slotOptionHtml(slot) {
  const style = optionStyleForSlotCategory(slot?.category);
  return `<option value="${slot.slot_key}" style="${style}">${slotLabel(slot)}</option>`;
}

export function renderBoardComposer(container, { onCreated, showPostComposer = true } = {}) {
  let selectedFiles = [];
  const me = getCurrentUser() || {};
  const canDirectPublish = isPublishManagerRole(me?.role);
  const isRoleUser = String(me?.role || '').trim() === 'user';
  container.innerHTML = `
    ${showPostComposer ? `
    <div class="card board-composer-card">
      <div class="card-title">Nuova comunicazione</div>
      <div class="form-group">
        <label>Titolo</label>
        <textarea id="board-title" class="input board-input-lg board-input-title" rows="2" maxlength="180" placeholder="Titolo comunicazione"></textarea>
      </div>
      <div class="form-group">
        <label>Contenuto</label>
        <textarea id="board-body" class="input board-input-lg board-input-body" rows="10" placeholder="Descrivi la comunicazione"></textarea>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label>Categoria</label>
          <select id="board-category" class="input">
            ${BOARD_CATEGORIES.map((c) => `<option value="${c.value}">${c.label}</option>`).join('')}
          </select>
        </div>
        <div class="form-group">
          <label>Priorità</label>
          <select id="board-priority" class="input">
            <option value="info">Informazione</option>
            <option value="important">Importante</option>
            <option value="urgent">Urgente</option>
          </select>
        </div>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label>Destinatari</label>
          <select id="board-target-type" class="input">
            <option value="all">Tutti</option>
            <option value="user">Singoli medici</option>
          </select>
        </div>
        <div class="form-group" id="board-target-users-wrap" style="display:none;">
          <label>Medici destinatari</label>
          <details id="board-target-users-details" class="board-users-picker">
            <summary class="board-users-picker-summary">Seleziona medici</summary>
            <div class="board-users-picker-actions">
              <button type="button" class="btn btn-outline btn-sm" id="board-target-users-select-all">Seleziona tutti</button>
              <button type="button" class="btn btn-outline btn-sm" id="board-target-users-clear-all">Deseleziona tutti</button>
            </div>
            <div id="board-target-users-list" class="board-users-picker-list"></div>
          </details>
          <div id="board-target-users-selected" class="board-users-picker-selected">Nessun medico selezionato.</div>
        </div>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label>Data/Ora evento (opzionale)</label>
          <input id="board-event-start-at" class="input" type="datetime-local" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-group">
          <label>Scadenza (opzionale)</label>
          <input id="board-expires-at" class="input" type="datetime-local" />
        </div>
        <div class="form-group">
          <label>Programma pubblicazione (opzionale)</label>
          <input id="board-scheduled-at" class="input" type="datetime-local" />
        </div>
      </div>
      <div class="form-group" style="display:flex; gap: 1rem; align-items:center;">
        <label><input id="board-requires-ack" type="checkbox" /> Richiede conferma lettura</label>
      </div>
      <div class="form-group">
        <label>Allegati (PDF, Excel, immagini, documenti)</label>
        <input id="board-attachments-input" class="input" type="file" multiple />
        <div id="board-attachments-list" class="board-attachments-list" style="margin-top:.45rem; color:var(--text-secondary); font-size:.85rem;">Nessun allegato selezionato.</div>
      </div>
      <div class="board-composer-actions">
        ${isRoleUser ? '' : '<button id="board-save-draft" class="btn btn-secondary">Salva bozza</button>'}
        <button id="board-submit-review" class="btn btn-outline">Invia in approvazione</button>
        ${canDirectPublish ? '<button id="board-publish" class="btn btn-primary">Pubblica</button>' : ''}
      </div>
      <div id="board-composer-msg" style="margin-top:.75rem; font-size:.85rem; color: var(--text-secondary);"></div>
    </div>
    ` : ''}
    <div class="card board-shift-change-card" id="board-shift-change-card" style="margin-top:${showPostComposer ? '1rem' : '0'}; display:none;">
      <div class="card-title">Richiesta cambio turno</div>
      <div class="form-group">
        <label>Il tuo turno da cambiare</label>
        <select id="shift-change-slot" class="input"></select>
      </div>
      <div class="form-group">
        <label>Medico con cui chiedere lo scambio</label>
        <select id="shift-change-target-user" class="input"></select>
      </div>
      <div class="form-group">
        <label>Turno di destinazione del medico selezionato</label>
        <select id="shift-change-target-slot" class="input"></select>
      </div>
      <div class="form-group">
        <label>Motivazione (opzionale)</label>
        <textarea id="shift-change-reason" class="input board-shift-reason-lg" rows="4" placeholder="Motivazione richiesta cambio turno"></textarea>
      </div>
      <div style="display:flex; gap:.5rem; align-items:center;">
        <button id="shift-change-submit" class="btn btn-primary">Invia richiesta</button>
      </div>
      <div id="shift-change-msg" style="margin-top:.7rem; font-size:.85rem; color:var(--text-secondary);"></div>
      <div id="shift-change-requests" style="margin-top:.85rem;"></div>
    </div>
  `;

  const targetTypeEl = container.querySelector('#board-target-type');
  const targetUsersWrapEl = container.querySelector('#board-target-users-wrap');
  const targetUsersDetailsEl = container.querySelector('#board-target-users-details');
  const targetUsersSummaryEl = container.querySelector('#board-target-users-details .board-users-picker-summary');
  const targetUsersSelectAllEl = container.querySelector('#board-target-users-select-all');
  const targetUsersClearAllEl = container.querySelector('#board-target-users-clear-all');
  const targetUsersListEl = container.querySelector('#board-target-users-list');
  const targetUsersSelectedEl = container.querySelector('#board-target-users-selected');
  const msgEl = container.querySelector('#board-composer-msg');
  const attachmentsInputEl = container.querySelector('#board-attachments-input');
  const attachmentsListEl = container.querySelector('#board-attachments-list');
  const shiftChangeCardEl = container.querySelector('#board-shift-change-card');
  const shiftChangeMsgEl = container.querySelector('#shift-change-msg');
  let targetUsers = [];
  const selectedTargetUserIds = new Set();

  const renderTargetUsersSummary = () => {
    if (targetUsersSummaryEl) targetUsersSummaryEl.textContent = 'Seleziona medici';
    if (!targetUsersSelectedEl) return;
    const selected = targetUsers.filter((u) => selectedTargetUserIds.has(String(u.id)));
    if (!selected.length) {
      targetUsersSelectedEl.textContent = 'Nessun medico selezionato.';
      return;
    }
    if (selected.length <= 3) {
      targetUsersSelectedEl.textContent = selected.map((u) => doctorLabelFromUser(u)).join(' · ');
      if (targetUsersSummaryEl) targetUsersSummaryEl.textContent = `Seleziona medici (${selected.length})`;
      return;
    }
    targetUsersSelectedEl.textContent = `${selected.length} medici selezionati.`;
    if (targetUsersSummaryEl) targetUsersSummaryEl.textContent = `Seleziona medici (${selected.length})`;
  };

  const renderTargetUsersChecklist = () => {
    if (!targetUsersListEl) return;
    if (!targetUsers.length) {
      targetUsersListEl.innerHTML = '<div class="board-users-picker-empty">Nessun medico disponibile.</div>';
      renderTargetUsersSummary();
      return;
    }
    targetUsersListEl.innerHTML = targetUsers.map((u) => `
      <label class="board-users-picker-item">
        <input type="checkbox" value="${escapeAttr(String(u.id))}" ${selectedTargetUserIds.has(String(u.id)) ? 'checked' : ''} />
        <span>${escapeHtml(doctorLabelFromUser(u))}</span>
      </label>
    `).join('');
    targetUsersListEl.querySelectorAll('input[type="checkbox"]').forEach((chk) => {
      chk.addEventListener('change', () => {
        const id = String(chk.value || '');
        if (!id) return;
        if (chk.checked) selectedTargetUserIds.add(id);
        else selectedTargetUserIds.delete(id);
        renderTargetUsersSummary();
      });
    });
    renderTargetUsersSummary();
  };

  targetTypeEl?.addEventListener('change', () => {
    targetUsersWrapEl.style.display = targetTypeEl.value === 'user' ? '' : 'none';
    if (targetTypeEl.value !== 'user' && targetUsersDetailsEl) {
      targetUsersDetailsEl.open = false;
    }
  });

  targetUsersSelectAllEl?.addEventListener('click', () => {
    targetUsers.forEach((u) => {
      const id = String(u?.id || '');
      if (id) selectedTargetUserIds.add(id);
    });
    renderTargetUsersChecklist();
  });

  targetUsersClearAllEl?.addEventListener('click', () => {
    selectedTargetUserIds.clear();
    renderTargetUsersChecklist();
  });

  attachmentsInputEl?.addEventListener('change', () => {
    selectedFiles = Array.from(attachmentsInputEl.files || []);
    if (!attachmentsListEl) return;
    if (selectedFiles.length === 0) {
      attachmentsListEl.textContent = 'Nessun allegato selezionato.';
      return;
    }
    attachmentsListEl.innerHTML = selectedFiles
      .map((f) => `${f.name} (${Math.round((f.size || 0) / 1024)} KB)`)
      .join('<br>');
  });

  async function submitWithStatus(status) {
    const title = container.querySelector('#board-title')?.value?.trim() || '';
    const body = container.querySelector('#board-body')?.value?.trim() || '';
    if (!title || !body) {
      msgEl.textContent = 'Titolo e contenuto sono obbligatori.';
      msgEl.style.color = 'var(--danger)';
      return;
    }
    const targetType = targetTypeEl?.value || 'all';
    const selectedUserTargets = targetType === 'user'
      ? Array.from(selectedTargetUserIds)
      : [];
    if (targetType === 'user' && selectedUserTargets.length === 0) {
      msgEl.textContent = 'Seleziona almeno un medico destinatario.';
      msgEl.style.color = 'var(--danger)';
      return;
    }
    let uploadedAttachments = [];
    const payload = {
      title,
      body,
      category: container.querySelector('#board-category')?.value?.trim() || 'comunicazioni',
      priority: container.querySelector('#board-priority')?.value || 'info',
      status,
      requiresAck: Boolean(container.querySelector('#board-requires-ack')?.checked),
      expiresAt: container.querySelector('#board-expires-at')?.value?.trim() || null,
      scheduledAt: container.querySelector('#board-scheduled-at')?.value?.trim() || null,
      eventStartAt: container.querySelector('#board-event-start-at')?.value?.trim() || null,
      eventEndAt: null,
      targets: targetType === 'user'
        ? selectedUserTargets.map((uid) => ({ targetType: 'user', targetValue: uid }))
        : [{ targetType: 'all', targetValue: 'all' }],
    };
    try {
      if (selectedFiles.length > 0) {
        msgEl.textContent = `Caricamento allegati (${selectedFiles.length})...`;
        msgEl.style.color = 'var(--text-secondary)';
        for (const file of selectedFiles) {
          const up = await apiUploadBoardAttachment(file);
          uploadedAttachments.push({
            fileName: up.fileName,
            url: up.url,
            mimeType: up.mimeType,
            sizeBytes: up.sizeBytes,
          });
        }
      }
      payload.attachments = uploadedAttachments;
      await apiCreateBoardPost(payload);
      msgEl.textContent = 'Comunicazione salvata correttamente.';
      msgEl.style.color = 'var(--success)';
      if (typeof onCreated === 'function') onCreated();
      container.querySelector('#board-title').value = '';
      container.querySelector('#board-body').value = '';
      selectedTargetUserIds.clear();
      renderTargetUsersChecklist();
      const eventStartEl = container.querySelector('#board-event-start-at');
      if (eventStartEl) eventStartEl.value = '';
      if (attachmentsInputEl) attachmentsInputEl.value = '';
      selectedFiles = [];
      if (attachmentsListEl) attachmentsListEl.textContent = 'Nessun allegato selezionato.';
    } catch (e) {
      msgEl.textContent = e?.message || 'Errore salvataggio comunicazione.';
      msgEl.style.color = 'var(--danger)';
    }
  }

  container.querySelector('#board-save-draft')?.addEventListener('click', () => submitWithStatus('draft'));
  container.querySelector('#board-submit-review')?.addEventListener('click', () => submitWithStatus('in_review'));
  container.querySelector('#board-publish')?.addEventListener('click', () => submitWithStatus('published'));

  async function initTargetUsers() {
    if (!targetUsersListEl) return;
    try {
      const users = await apiGetBoardTargetUsers();
      targetUsers = Array.isArray(users) ? users : [];
      const availableIds = new Set(targetUsers.map((u) => String(u.id)));
      Array.from(selectedTargetUserIds).forEach((id) => {
        if (!availableIds.has(id)) selectedTargetUserIds.delete(id);
      });
      renderTargetUsersChecklist();
    } catch (_) {
      targetUsers = [];
      selectedTargetUserIds.clear();
      renderTargetUsersChecklist();
    }
  }

  async function initShiftChangeCard() {
    const me = getCurrentUser() || {};
    const meUserId = String(me?.userId || me?.id || '');
    const meDoctorId = String(me?.doctorId || me?.doctor_id || '').trim();
    if (!shiftChangeCardEl) return;
    if (!meDoctorId) return;
    shiftChangeCardEl.style.display = '';
    const slotSel = container.querySelector('#shift-change-slot');
    const targetSel = container.querySelector('#shift-change-target-user');
    const targetSlotSel = container.querySelector('#shift-change-target-slot');
    const reasonEl = container.querySelector('#shift-change-reason');
    const submitBtn = container.querySelector('#shift-change-submit');
    const requestsListEl = container.querySelector('#shift-change-requests');
    let targetSlotsByUser = {};

    const refreshRequestsList = async () => {
      if (!requestsListEl) return;
      try {
        const rows = await apiListShiftChangeRequests();
        const allRows = Array.isArray(rows) ? rows : [];
        const visibleStatuses = new Set(['pending_peer', 'pending', 'approved']);
        const list = allRows.filter((r) => {
          const status = String(r?.status || '').trim().toLowerCase();
          if (!visibleStatuses.has(status)) return false;
          const requesterDoc = String(r?.requester_doctor_id || '').trim();
          const requestedDoc = String(r?.requested_doctor_id || '').trim();
          return requesterDoc === meDoctorId || requestedDoc === meDoctorId;
        });
        if (!list.length) {
          requestsListEl.innerHTML = '<div style="color:var(--text-secondary); font-size:.85rem;">Nessuna richiesta cambio turno del tuo profilo medico.</div>';
          return;
        }
        requestsListEl.innerHTML = `
          <div style="font-weight:600; margin-bottom:.4rem;">Richieste cambio turno</div>
          <div style="display:grid; gap:.55rem;">
            ${list.map((r) => `
              <article class="board-item board-item--compact" data-shift-item-id="${r.id}">
                <div style="display:flex; justify-content:space-between; gap:.5rem; align-items:center;">
                  <strong>${statusLabel(r.status)}</strong>
                  <span class="badge shift-status-badge shift-status-badge--${statusClass(r.status)}">${statusLabel(r.status)}</span>
                </div>
                <div style="font-size:.85rem; color:var(--text-secondary); margin-top:.25rem;">
                  Richiedente: ${escapeHtml(doctorLabelFromUser({ username: r.requester_username, sigla: r.requester_sigla }))} · Destinatario: ${escapeHtml(doctorLabelFromUser({ username: r.requested_username, sigla: r.requested_sigla }))}
                </div>
                <div style="margin-top:.35rem; display:flex; flex-wrap:wrap; gap:.3rem; align-items:center;">
                  <span style="font-size:.8rem; color:var(--text-secondary);">Prima:</span>
                  <span class="badge shift-person-badge">${escapeHtml(personBadgeLabel(r.requester_username || r.requester_user_id, r.requester_sigla))}</span>
                  ${slotBadgeHtml({ date: r.slot_date, time_slot: r.slot_time_slot, category: r.slot_category, name: r.slot_name })}
                  <span class="badge shift-person-badge">${escapeHtml(personBadgeLabel(r.requested_username || r.requested_user_id, r.requested_sigla))}</span>
                  ${slotBadgeHtml({ date: r.target_slot_date, time_slot: r.target_slot_time_slot, category: r.target_slot_category, name: r.target_slot_name })}
                </div>
                <div style="margin-top:.25rem; display:flex; flex-wrap:wrap; gap:.3rem; align-items:center;">
                  <span style="font-size:.8rem; color:var(--text-secondary);">Ora:</span>
                  <span class="badge shift-person-badge">${escapeHtml(personBadgeLabel(r.requested_username || r.requested_user_id, r.requested_sigla))}</span>
                  ${slotBadgeHtml({ date: r.slot_date, time_slot: r.slot_time_slot, category: r.slot_category, name: r.slot_name })}
                  <span class="badge shift-person-badge">${escapeHtml(personBadgeLabel(r.requester_username || r.requester_user_id, r.requester_sigla))}</span>
                  ${slotBadgeHtml({ date: r.target_slot_date, time_slot: r.target_slot_time_slot, category: r.target_slot_category, name: r.target_slot_name })}
                </div>
                ${String(r.reason || '').trim() ? `<div style="margin-top:.3rem;">Motivazione: ${escapeHtml(String(r.reason).slice(0, 220))}</div>` : ''}
                ${(String(r.status || '') === 'pending_peer' && String(r.requested_user_id || '') === meUserId) ? `
                  <div style="display:flex; gap:.45rem; margin-top:.55rem;">
                    <button type="button" class="btn btn-primary btn-sm" data-peer-action="approve">Conferma proposta</button>
                    <button type="button" class="btn btn-outline btn-sm" data-peer-action="reject">Rifiuta proposta</button>
                  </div>
                ` : ''}
                ${isClosedShiftRequest(r.status) ? `
                  <div style="display:flex; gap:.45rem; margin-top:.55rem;">
                    <button type="button" class="btn btn-outline btn-sm" data-shift-action="dismiss">Rimuovi dal mio elenco</button>
                  </div>
                ` : ''}
              </article>
            `).join('')}
          </div>
        `;
        requestsListEl.querySelectorAll('[data-peer-action]').forEach((btn) => {
          btn.addEventListener('click', async () => {
            const article = btn.closest('[data-shift-item-id]');
            const reqId = article?.dataset?.shiftItemId;
            const action = btn.dataset.peerAction;
            if (!reqId) return;
            const note = window.prompt(action === 'approve' ? 'Nota al Direttore (opzionale):' : 'Motivo rifiuto (opzionale):', '') || '';
            try {
              if (action === 'approve') await apiPeerApproveShiftChangeRequest(reqId, note);
              if (action === 'reject') await apiPeerRejectShiftChangeRequest(reqId, note);
              await refreshRequestsList();
            } catch (e) {
              shiftChangeMsgEl.textContent = e?.message || 'Errore operazione richiesta cambio turno.';
              shiftChangeMsgEl.style.color = 'var(--danger)';
            }
          });
        });
        requestsListEl.querySelectorAll('[data-shift-action="dismiss"]').forEach((btn) => {
          btn.addEventListener('click', async () => {
            const article = btn.closest('[data-shift-item-id]');
            const reqId = article?.dataset?.shiftItemId;
            if (!reqId) return;
            try {
              await apiDismissShiftChangeRequest(reqId);
              await refreshRequestsList();
            } catch (e) {
              shiftChangeMsgEl.textContent = e?.message || 'Errore rimozione richiesta dal tuo elenco.';
              shiftChangeMsgEl.style.color = 'var(--danger)';
            }
          });
        });
      } catch (e) {
        requestsListEl.innerHTML = `<div style="color:var(--danger); font-size:.85rem;">${escapeHtml(e?.message || 'Errore caricamento richieste')}</div>`;
      }
    };

    try {
      const data = await apiGetShiftChangeOptions();
      const slots = data?.slots || [];
      const users = data?.targetUsers || [];
      targetSlotsByUser = data?.targetSlotsByUser || {};
      slotSel.innerHTML = slots.length
        ? slots.map((s) => slotOptionHtml(s)).join('')
        : '<option value="">Nessun turno disponibile</option>';
      targetSel.innerHTML = users.length
        ? users.map((u) => `<option value="${u.id}">${doctorLabelFromUser(u)}</option>`).join('')
        : '<option value="">Nessun medico disponibile</option>';
      const fillTargetSlots = () => {
        const targetUid = targetSel?.value || '';
        const tSlots = targetSlotsByUser[targetUid] || [];
        targetSlotSel.innerHTML = tSlots.length
          ? tSlots.map((s) => slotOptionHtml(s)).join('')
          : '<option value="">Nessun turno disponibile per il medico selezionato</option>';
      };
      targetSel?.addEventListener('change', fillTargetSlots);
      fillTargetSlots();
    } catch (e) {
      shiftChangeMsgEl.textContent = e?.message || 'Errore caricamento opzioni cambio turno.';
      shiftChangeMsgEl.style.color = 'var(--danger)';
      return;
    }
    submitBtn?.addEventListener('click', async () => {
      const slotKey = slotSel?.value || '';
      const requestedUserId = targetSel?.value || '';
      const targetSlotKey = targetSlotSel?.value || '';
      if (!slotKey || !requestedUserId || !targetSlotKey) {
        shiftChangeMsgEl.textContent = 'Seleziona turno sorgente, medico destinatario e turno di destinazione.';
        shiftChangeMsgEl.style.color = 'var(--danger)';
        return;
      }
      try {
        await apiCreateShiftChangeRequest({
          slotKey,
          targetSlotKey,
          requestedUserId,
          reason: reasonEl?.value?.trim() || null,
        });
        shiftChangeMsgEl.textContent = 'Richiesta inviata.';
        shiftChangeMsgEl.style.color = 'var(--success)';
        if (reasonEl) reasonEl.value = '';
        await refreshRequestsList();
      } catch (e) {
        shiftChangeMsgEl.textContent = e?.message || 'Errore invio richiesta cambio turno.';
        shiftChangeMsgEl.style.color = 'var(--danger)';
      }
    });
    await refreshRequestsList();
  }

  if (showPostComposer) void initTargetUsers();
  void initShiftChangeCard();
}
